import { textBox, type TextMeasure } from './markupBounds.ts';
import { boxContains, inflateBox } from './markupConstraints.ts';
import { arrowGeometry, distance } from './markupGeometry.ts';
import type { ArrowShape, Box, BoxShape, MarkupShape, Point } from './markupShapes.ts';

// In image pixels: how far off a thin stroke still counts, below which size a shape's
// inside counts too, and one screen pixel.
export interface HitOptions {
    tolerance: number;
    smallShape: number;
    screenPx: number;
}

const HIT_TOLERANCE_SCREEN_PX = 6;
const SMALL_SHAPE_SCREEN_PX = 40;
const DEGENERATE_RADIUS = 1e-6;

export function hitOptionsFor(scale: number): HitOptions {
    const perScreenPx = scale > 0 ? 1 / scale : 1;
    return {
        tolerance: HIT_TOLERANCE_SCREEN_PX * perScreenPx,
        smallShape: SMALL_SHAPE_SCREEN_PX * perScreenPx,
        screenPx: perScreenPx
    };
}

export function distanceToSegment(point: Point, a: Point, b: Point): number {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const lengthSquared = dx * dx + dy * dy;
    if (lengthSquared === 0) return distance(point, a);
    const t = ((point.x - a.x) * dx + (point.y - a.y) * dy) / lengthSquared;
    const clamped = Math.min(1, Math.max(0, t));
    return distance(point, { x: a.x + dx * clamped, y: a.y + dy * clamped });
}

function distanceToPolyline(point: Point, points: readonly Point[]): number {
    if (points.length === 1) return distance(point, points[0]);
    return points
        .slice(1)
        .reduce((best, to, i) => Math.min(best, distanceToSegment(point, points[i], to)), Infinity);
}

function distanceToRectOutline(point: Point, box: Box): number {
    const right = box.x + box.width;
    const bottom = box.y + box.height;
    if (boxContains(box, point)) {
        return Math.min(point.x - box.x, right - point.x, point.y - box.y, bottom - point.y);
    }
    const dx = Math.max(box.x - point.x, 0, point.x - right);
    const dy = Math.max(box.y - point.y, 0, point.y - bottom);
    return Math.hypot(dx, dy);
}

// Measured along the ray from the centre: exact for circles, close enough for ovals.
function distanceToEllipseOutline(point: Point, box: Box): number {
    const rx = box.width / 2;
    const ry = box.height / 2;
    const center = { x: box.x + rx, y: box.y + ry };
    if (rx < DEGENERATE_RADIUS || ry < DEGENERATE_RADIUS) {
        return distanceToSegment(
            point,
            { x: box.x, y: box.y },
            { x: box.x + box.width, y: box.y + box.height }
        );
    }
    const dx = point.x - center.x;
    const dy = point.y - center.y;
    const fromCenter = Math.hypot(dx, dy);
    if (fromCenter === 0) return Math.min(rx, ry);
    const radius = (rx * ry) / Math.hypot((ry * dx) / fromCenter, (rx * dy) / fromCenter);
    return Math.abs(fromCenter - radius);
}

function insideEllipse(point: Point, box: Box): boolean {
    const rx = box.width / 2;
    const ry = box.height / 2;
    if (rx < DEGENERATE_RADIUS || ry < DEGENERATE_RADIUS) return false;
    const nx = (point.x - box.x - rx) / rx;
    const ny = (point.y - box.y - ry) / ry;
    return nx * nx + ny * ny <= 1;
}

function cross(o: Point, a: Point, b: Point): number {
    return (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
}

function insideTriangle(point: Point, a: Point, b: Point, c: Point): boolean {
    const d1 = cross(a, b, point);
    const d2 = cross(b, c, point);
    const d3 = cross(c, a, point);
    const hasNegative = d1 < 0 || d2 < 0 || d3 < 0;
    const hasPositive = d1 > 0 || d2 > 0 || d3 > 0;
    return !(hasNegative && hasPositive);
}

function hitsArrowHead(shape: ArrowShape, point: Point): boolean {
    const head = arrowGeometry(shape.from, shape.to, shape.width);
    return head != null && insideTriangle(point, head.tip, head.left, head.right);
}

function hitsBoxShape(shape: BoxShape, point: Point, options: HitOptions): boolean {
    const reach = shape.width / 2 + options.tolerance;
    const isSmall = Math.min(shape.box.width, shape.box.height) < options.smallShape;
    if (shape.kind === 'rect') {
        return (
            distanceToRectOutline(point, shape.box) <= reach ||
            (isSmall && boxContains(shape.box, point))
        );
    }
    return (
        distanceToEllipseOutline(point, shape.box) <= reach ||
        (isSmall && insideEllipse(point, shape.box))
    );
}

export function hitsShape(
    shape: MarkupShape,
    point: Point,
    options: HitOptions,
    measure: TextMeasure
): boolean {
    switch (shape.kind) {
        case 'pen':
            return distanceToPolyline(point, shape.points) <= shape.width / 2 + options.tolerance;
        case 'line':
            return (
                distanceToSegment(point, shape.from, shape.to) <=
                shape.width / 2 + options.tolerance
            );
        case 'arrow':
            return (
                distanceToSegment(point, shape.from, shape.to) <=
                    shape.width / 2 + options.tolerance || hitsArrowHead(shape, point)
            );
        case 'rect':
        case 'ellipse':
            return hitsBoxShape(shape, point, options);
        case 'text':
            return boxContains(inflateBox(textBox(shape, measure), options.tolerance), point);
    }
}

export function topmostHit(
    shapes: readonly MarkupShape[],
    point: Point,
    options: HitOptions,
    measure: TextMeasure
): MarkupShape | null {
    for (let i = shapes.length - 1; i >= 0; i -= 1) {
        if (hitsShape(shapes[i], point, options, measure)) return shapes[i];
    }
    return null;
}
